import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowUpRight, Search, Trash2, Users } from 'lucide-react';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import ConfirmDialog from './ConfirmDialog';
import { cn } from '../../lib/cn';
import { useAuth } from '../../hooks/useAuth';
import { deleteAdminUser } from '../../utils/api';
import { roleBadge, relativeTime, formatDate } from '../../utils/commandCenterHelpers';

const SORTS = {
  recent: { label: 'Recent activity', cmp: (a, b) => activityTime(b) - activityTime(a) },
  saves: { label: 'Most saves', cmp: (a, b) => (b.saveCount || 0) - (a.saveCount || 0) },
  name: { label: 'Name A–Z', cmp: (a, b) => a.name.localeCompare(b.name) }
};

function activityTime(user) {
  return user.lastActivityAt ? new Date(user.lastActivityAt).getTime() : 0;
}

export default function TeamDirectory({ users, loading, onChanged }) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [sort, setSort] = useState('recent');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAdminUser(deleteTarget.id);
      toast.success(`Deleted @${deleteTarget.username}`);
      setDeleteTarget(null);
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  const visibleUsers = useMemo(() => {
    let list = [...users];
    const query = search.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (u) =>
          u.name.toLowerCase().includes(query) ||
          u.username.toLowerCase().includes(query)
      );
    }
    if (role !== 'all') {
      list = list.filter((u) => u.role === role);
    }
    list.sort(SORTS[sort].cmp);
    return list;
  }, [users, search, role, sort]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden">
      {/* dark gradient header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-brand-800 px-5 py-4 text-white">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-500/30 blur-2xl" />
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="h-8 w-8 rounded-xl bg-white/10 border border-white/15 inline-flex items-center justify-center">
              <Users className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-[15px] font-semibold leading-tight">Team directory</h2>
              <p className="text-[12px] text-slate-300">Every account and their footprint</p>
            </div>
          </div>
          <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-white/10 border border-white/15 text-[11.5px] font-semibold tabular-nums">
            {users.length} profiles
          </span>
        </div>
      </div>

      {/* controls */}
      <div className="px-5 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row gap-2.5">
        <Input
          leftIcon={Search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or username"
          className="sm:flex-1"
        />
        <Select value={role} onChange={(e) => setRole(e.target.value)} className="sm:w-40">
          <option value="all">All roles</option>
          <option value="superadmin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="viewer">Viewer</option>
        </Select>
        <Select value={sort} onChange={(e) => setSort(e.target.value)} className="sm:w-44">
          {Object.entries(SORTS).map(([key, { label }]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {/* grid */}
      <div className="p-4">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[132px] rounded-xl" />
            ))}
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[14px] font-medium text-slate-700">
              {users.length === 0 ? 'No accounts yet' : 'No matching profiles'}
            </p>
            <p className="text-[12.5px] text-slate-500 mt-1">
              {users.length === 0
                ? 'Provision an account from the panel on the right.'
                : 'Try a different search or role filter.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {visibleUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onOpen={() => navigate(`/command-center/users/${user.id}`)}
                onDelete={user.id === me?.id ? null : () => setDeleteTarget(user)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete account"
        confirmLabel="Delete permanently"
        description={deleteTarget ? `@${deleteTarget.username} will be permanently removed.` : ''}
      >
        <p className="text-[13px] text-slate-600">
          Their authored version history is retained for audit, but the account cannot be recovered.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function UserCard({ user, onOpen, onDelete }) {
  const badge = roleBadge(user.role);
  const hasActivity = user.saveCount > 0;

  return (
    <div
      className={cn(
        'group relative rounded-xl border border-slate-200 bg-white transition-all duration-150',
        'hover:border-brand-200 hover:shadow-lift focus-within:ring-2 focus-within:ring-brand-500'
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-3.5 focus:outline-none"
      >
        <div className="flex items-start gap-3">
          <Avatar name={user.name} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-[14px] font-semibold text-slate-900 truncate">{user.name}</p>
              <Badge variant={badge.variant} size="sm">
                {badge.label}
              </Badge>
            </div>
            <p className="text-[12.5px] text-slate-500 truncate">@{user.username}</p>
          </div>
          {/* spacer so the title row never sits under the corner actions */}
          <span className="w-9 shrink-0" />
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <MiniStat label="Saves" value={user.saveCount} dim={!hasActivity} />
          <MiniStat label="Entities" value={user.entitiesTouchedCount} dim={!hasActivity} />
          <MiniStat
            label="Last activity"
            value={hasActivity ? relativeTime(user.lastActivityAt) : 'Never'}
            dim={!hasActivity}
            small
          />
        </div>

        <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <span>Created {formatDate(user.createdAt)}</span>
          <span>Login {user.lastLoginAt ? relativeTime(user.lastLoginAt) : '—'}</span>
        </div>
      </button>

      {/* corner actions (siblings of the main button, so no nested buttons) */}
      <div className="absolute top-2.5 right-2.5 flex items-center gap-1">
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label={`Delete @${user.username}`}
            title="Delete account"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-300 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-rose-50 hover:text-rose-600 transition-all"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
        <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-brand-500 transition-colors pointer-events-none" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, dim, small }) {
  return (
    <div
      className={cn(
        'rounded-lg px-2 py-1.5 bg-slate-50 border border-slate-100',
        dim && 'opacity-70'
      )}
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p
        className={cn(
          'font-semibold tabular-nums truncate',
          small ? 'text-[12px]' : 'text-[15px]',
          dim ? 'text-slate-400' : 'text-slate-900'
        )}
      >
        {value}
      </p>
    </div>
  );
}
